import { Link } from 'react-router-dom';
import { Loader2, Printer, PrinterCheck, Unplug } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { usePrinterStatus } from '@/shared/hooks/usePrinterStatus';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';

/**
 * Compact pill in the top bar showing whether a printer is reachable.
 * Renders nothing when the app is opened in a browser (only in Electron).
 * Click anywhere on the pill jumps to the printer settings page.
 *
 * Note the deliberate difference between "connected" (network probe — we know
 * the printer is *responding*) and "installed" (OS reports a printer but
 * cannot tell if it's powered on). The badge is honest about this.
 */
export function PrinterStatusBadge() {
  const { status, printerName, printerCount, error, latencyMs, rawOsStatus, rawOsStatusLabel } =
    usePrinterStatus();

  if (status === 'browser') return null;

  const palette = {
    checking: {
      icon: Loader2,
      label: 'Checking…',
      tooltip: 'Looking for printers',
      classes: 'border-border bg-muted text-muted-foreground hover:bg-muted/80',
      iconClasses: 'animate-spin',
    },
    connected: {
      icon: PrinterCheck,
      label: printerName ?? 'Printer ready',
      tooltip: (() => {
        const detail = latencyMs != null ? ` (${latencyMs} ms)` : '';
        return `Printer connected: ${printerName}${detail}`;
      })(),
      classes:
        'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-400',
      iconClasses: '',
    },
    installed: {
      icon: Printer,
      label: printerName ? `Installed: ${printerName}` : 'Printer installed',
      tooltip: (() => {
        const base =
          printerCount > 1
            ? `${printerCount} printers installed at the OS level.`
            : `Printer is installed at the OS level.`;
        const detail =
          rawOsStatus != null
            ? ` OS status: ${rawOsStatusLabel ?? `code ${rawOsStatus}`}.`
            : '';
        return `${base}${detail} We can't reliably tell whether it's powered on — switch to Network mode for live status.`;
      })(),
      classes:
        'border-sky-500/30 bg-sky-500/10 text-sky-700 hover:bg-sky-500/15 dark:text-sky-400',
      iconClasses: '',
    },
    'no-printer': {
      icon: Printer,
      label: 'No printer',
      tooltip:
        'No OS-installed printer detected. Click to configure a network printer.',
      classes:
        'border-amber-500/30 bg-amber-500/10 text-amber-700 hover:bg-amber-500/15 dark:text-amber-400',
      iconClasses: '',
    },
    unreachable: {
      icon: Unplug,
      label: 'Printer offline',
      tooltip: error
        ? `${printerName ?? 'Configured printer'} not responding: ${error}. Likely powered off or wrong IP — click to fix.`
        : `${printerName ?? 'Configured printer'} not responding. Likely powered off — click to fix.`,
      classes:
        'border-rose-500/30 bg-rose-500/10 text-rose-700 hover:bg-rose-500/15 dark:text-rose-400',
      iconClasses: '',
    },
    disabled: {
      icon: Printer,
      label: 'Printer off',
      tooltip: 'Printing is disabled. Click to enable.',
      classes: 'border-border bg-muted text-muted-foreground hover:bg-muted/80',
      iconClasses: '',
    },
  } as const;

  const cfg = palette[status as keyof typeof palette];
  const Icon = cfg.icon;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to="/app/settings/printer"
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 h-8 text-xs font-medium select-none transition-colors',
              cfg.classes,
            )}
            role="status"
            aria-label={cfg.tooltip}
          >
            <Icon className={cn('h-3.5 w-3.5 shrink-0', cfg.iconClasses)} />
            <span className="hidden sm:inline truncate max-w-[160px]">{cfg.label}</span>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="bottom">{cfg.tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
